import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faEdit, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';

import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { extractFoodCategoryId, extractFoodCategoryName, normalizeIngredientOptions } from './restaurantApi';
import defaultFoodImage from './assets/DefaultFood.jpg';
import {
  loadGroupedOnlineMenuItems,
  formatDietTypeLabel,
  formatFoodItemDisplayName,
  formatMenuItemStatusLabel,
  formatOnlineIngredientsDisplay,
  formatIngredientsTablePreview,
  isMenuItemActiveStatus,
  normalizeDietType,
    resolveFoodItemImageFields,
    resolveIngredientIdsFromMenuItem,
    resolveChannelFlag,
    GROUP_COURSE_ROLES,
    GROUP_SERVING_UNITS,
    mapGroupScaleFromApi,
} from './onlineFoodItems';
import { fetchFoodItemRecipe } from './recipeApi';
import { recipeResponseHasContent, recipeIngredientsListFromResponse } from './foodItemRecipeUtils';
import SearchableSelect from './SearchableSelect';
import IngredientsMultiSelect from './IngredientsMultiSelect';
import OrdersSortableTh from './OrdersSortableTh';
import OrdersTablePanel from './OrdersTablePanel';
import OrdersTablePagination from './OrdersTablePagination';
import ToolbarRichSelect from './ToolbarRichSelect';
import CollapsibleFilters from './CollapsibleFilters';
import {
    buildMenuCategoryFilterOptions,
    MENU_FOOD_TYPE_FILTER_OPTIONS,
    MENU_STATUS_FILTER_OPTIONS,
} from './menuFilterOptions';
import { processMenuImageFile } from './menuImageUtils';
import MasterMenuPicker from './MasterMenuPicker';
import AgePricingEditor, {
    normalizeAgePricingForSubmit,
    parseAgePricingFromItem,
} from './AgePricingEditor';
import { AgePricingComboCell } from './AgePricingDisplay';
import NutritionEditor, { parseNutritionRowsFromItem } from './NutritionEditor';

import "./css/Menu.css";
import './css/Inventory.css';
import './css/AgePricingEditor.css';
import './css/AgePricingDisplay.css';
import './css/NutritionEditor.css';
import './css/OrdersTablePagination.css';
import './css/TodayOrder.css';

function isCateringItemAvailable(item) {
    const v = item?.isAvailable ?? item?.Is_available ?? item?.is_available;
    if (v === true || v === 1 || v === '1') return true;
    if (v === false || v === 0 || v === '0') return false;
    return true;
}

function formatAvailabilityLabel(item) {
    return isCateringItemAvailable(item) ? 'Yes' : 'No';
}

function getMenuRowSortValue(row, sortKey) {
    const { item, category } = row;
    switch (sortKey) {
        case 'foodItem':
            return String(item.Food_Item ?? '').trim();
        case 'category':
            return String(category ?? '').trim();
        case 'price':
            return Number.parseFloat(item.Price) || 0;
        case 'quantity': {
            const q = item.Quantity;
            const n = Number.parseFloat(q);
            return Number.isFinite(n) ? n : String(q ?? '').trim();
        }
        case 'foodType':
            return String(normalizeDietType(item.itemFoodType) ?? '').trim();
        case 'menuSource':
            return String(item.Menu_Source ?? '').trim();
        case 'description':
            return String(item.Description ?? item.description ?? item.Notes ?? '').trim();
        case 'ingredients':
            return String(formatOnlineIngredientsDisplay(item) ?? '').trim();
        case 'status':
            return String(item.Status ?? item.status ?? '').trim().toUpperCase();
        case 'available':
            return isCateringItemAvailable(item) ? 1 : 0;
        default:
            return '';
    }
}

function compareMenuRows(a, b, sortKey, direction) {
    const va = getMenuRowSortValue(a, sortKey);
    const vb = getMenuRowSortValue(b, sortKey);
    let cmp = 0;
    if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
    } else {
        cmp = String(va).localeCompare(String(vb), undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    }
    return direction === 'asc' ? cmp : -cmp;
}

function getCurrencySymbol(currency) {
    return String(currency ?? 'INR').toUpperCase() === 'USD' ? '$' : '₹';
}

function formatPriceWithCurrency(price, currency) {
    if (price == null || price === '') return '';
    return `${getCurrencySymbol(currency)}${price}`;
}

function getMenuItemRecipeId(item) {
    return Number(item?.Food_Item_Id ?? item?.cateringItemId ?? item?.foodItemId ?? 0);
}

function ingredientsListFromMenuItem(item) {
    if (!item) return [];
    if (Array.isArray(item.ingredients) && item.ingredients.length > 0) {
        return item.ingredients
            .map((ing) => ing.ingredientName || ing.ingredient_name || ing.name || '')
            .filter(Boolean);
    }
    const display = formatOnlineIngredientsDisplay(item);
    if (!display) return [];
    return display
        .split(/[,;]/)
        .map((part) => part.trim())
        .filter(Boolean);
}

function getMenuItemFoodCategory(item, categoryKey = '') {
    const fromItem =
        item?.foodCategory ??
        item?.Food_Category ??
        item?.food_category ??
        '';
    if (fromItem != null && String(fromItem).trim() !== '') {
        return String(fromItem).trim();
    }
    if (categoryKey != null && String(categoryKey).trim() !== '') {
        return String(categoryKey).trim();
    }
    const legacy = item?.Food_Type ?? item?.foodType ?? '';
    return legacy != null && String(legacy).trim() !== '' ? String(legacy).trim() : 'Other';
}

function menuItemMatchesSearch(item, queryLower) {
    if (!queryLower) return true;
    const fields = [
        item.Food_Item,
        item.foodCategory,
        item.Food_Category,
        item.Food_Type,
        item.Price,
        item.Quantity,
        item.Notes,
        item.Description,
        item.description,
        item.Menu_Source,
        item.Status,
        item.status,
        formatOnlineIngredientsDisplay(item),
        formatAvailabilityLabel(item),
    ];
    return fields.some((v) => String(v ?? '').toLowerCase().includes(queryLower));
}

function menuItemMatchesStatusFilter(item, statusFilter, isCateringMenu = false) {
    if (!statusFilter) return true;
    const isActive = isCateringMenu
        ? isCateringItemAvailable(item)
        : isMenuItemActiveStatus(item.Status ?? item.status);
    if (statusFilter === 'ACTIVE') return isActive;
    if (statusFilter === 'INACTIVE') return !isActive;
    return true;
}

const Menu = ({
    showAddMenu,
    newMenuItem,
    handleInputChange,
    handleAddNewItem,
    handleCloseAddMenu,
    menuItems,
    setMenuItems,
    editingItem,
    handleEditClick,
    handleDeleteMenuItem,
    handleUpdateItem,
    handleCancelEdit,
    foodCategories,
    restaurantId,
    handleAddMenu,
    menuTypeFilter,
    menuSource = 'MYEATS',
    salesChannel = '',
    onRecipeNavigate,
    onFoodItemNavigate,
}) => {
    const [ingredientOptions, setIngredientOptions] = useState([]);
    const [ingredientsLoading, setIngredientsLoading] = useState(false);
    const [foodItemSearch, setFoodItemSearch] = useState('');
    const [foodTypeFilter, setFoodTypeFilter] = useState('');
    const [foodCategoryFilter, setFoodCategoryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [recipeExistsByItemId, setRecipeExistsByItemId] = useState({});
    const [recipeIngredientsByItemId, setRecipeIngredientsByItemId] = useState({});
    const [menuPage, setMenuPage] = useState(1);
    const [menuPageSize, setMenuPageSize] = useState(10);
    const [menuSort, setMenuSort] = useState(null);
    const menuSourceFilter = useMemo(() => {
        const source = String(menuSource ?? 'MYEATS').trim().toUpperCase().replace(/-/g, '');
        if (source === 'JREATS') return 'JREATS';
        if (source === 'DINEIN') return 'DINEIN';
        return 'MYEATS';
    }, [menuSource]);
    const isJreatsCatalogReadOnly = menuSourceFilter === 'JREATS';
    const salesChannelFilter = String(salesChannel ?? '').trim().toLowerCase();
    const isOnlineMenu = menuTypeFilter === 'online';
    const isCateringMenu = menuTypeFilter === 'catering';
    const [jreatsMenuMeta, setJreatsMenuMeta] = useState(null);
    const [menuSourceLoadError, setMenuSourceLoadError] = useState('');
    const [ingredientsPopup, setIngredientsPopup] = useState(null);
    const [showMasterPicker, setShowMasterPicker] = useState(false);

    const foodSearchQuery = foodItemSearch.trim().toLowerCase();

    const createdById = useMemo(() => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || 'null');
            return user?.userId ?? user?.User_Id ?? user?.id ?? null;
        } catch {
            return null;
        }
    }, []);

    const canUseMasterPicker =
        isOnlineMenu &&
        !isJreatsCatalogReadOnly &&
        !isCateringMenu &&
        restaurantId;

    const setRecipeExistsForItem = (itemId, exists) => {
        const id = Number(itemId);
        if (!id) return;
        setRecipeExistsByItemId((prev) => ({ ...prev, [String(id)]: !!exists }));
    };

    const menuItemHasRecipe = (item) =>
        recipeExistsByItemId[String(getMenuItemRecipeId(item))] === true;

    useEffect(() => {
        setRecipeExistsByItemId({});
        setRecipeIngredientsByItemId({});
    }, [restaurantId, menuTypeFilter, menuSourceFilter]);

    useEffect(() => {
        let cancelled = false;

        const itemIds = [];
        Object.values(menuItems || {}).forEach((arr) => {
            if (!Array.isArray(arr)) return;
            arr.forEach((item) => {
                const id = getMenuItemRecipeId(item);
                if (id > 0 && !itemIds.includes(id)) itemIds.push(id);
            });
        });

        if (!itemIds.length) return undefined;

        itemIds.forEach((id) => {
            fetchFoodItemRecipe(
                menuTypeFilter === 'catering'
                    ? { cateringItemId: id }
                    : { foodItemId: id }
            )
                .then((data) => {
                    if (cancelled) return;
                    setRecipeExistsForItem(id, recipeResponseHasContent(data));
                    if (menuTypeFilter !== 'catering') {
                        setRecipeIngredientsByItemId((prev) => ({
                            ...prev,
                            [String(id)]: recipeIngredientsListFromResponse(data),
                        }));
                    }
                })
                .catch(() => {
                    if (cancelled) return;
                    setRecipeExistsForItem(id, false);
                    setRecipeIngredientsByItemId((prev) => ({
                        ...prev,
                        [String(id)]: [],
                    }));
                });
        });

        return () => {
            cancelled = true;
        };
    }, [menuItems, menuTypeFilter]);

    const displayMenuItems = useMemo(() => {
        let src = menuItems || {};
        if (menuTypeFilter === 'online') {
            const wanted = menuSourceFilter === 'JREATS' ? 'JREATS' : 'MYEATS';
            const bySource = {};
            Object.keys(src).forEach((categoryKey) => {
                const arr = Array.isArray(src[categoryKey]) ? src[categoryKey] : [];
                const matched = arr.filter((item) => {
                    const itemSrc = String(
                        item.Menu_Source ?? item.menuSource ?? item.menu_source ?? 'MYEATS'
                    )
                        .trim()
                        .toUpperCase();
                    return itemSrc === wanted;
                });
                if (matched.length > 0) {
                    bySource[categoryKey] = matched;
                }
            });
            src = bySource;
        }
        const foodTypeValue = normalizeDietType(foodTypeFilter);
        const hasSearch = Boolean(foodSearchQuery);
        const hasFoodTypeFilter = Boolean(foodTypeValue);
        const hasCategoryFilter = Boolean(foodCategoryFilter);
        const hasStatusFilter = Boolean(statusFilter);
        if (!hasSearch && !hasFoodTypeFilter && !hasCategoryFilter && !hasStatusFilter) return src;
        const out = {};
        Object.keys(src).forEach((categoryKey) => {
            if (hasCategoryFilter && categoryKey !== foodCategoryFilter) return;
            const arr = Array.isArray(src[categoryKey]) ? src[categoryKey] : [];
            const filtered = arr.filter((item) => {
                if (hasSearch && !menuItemMatchesSearch(item, foodSearchQuery)) return false;
                if (hasFoodTypeFilter && normalizeDietType(item.itemFoodType) !== foodTypeValue) return false;
                if (hasStatusFilter && !menuItemMatchesStatusFilter(item, statusFilter, isCateringMenu)) return false;
                return true;
            });
            if (filtered.length > 0) {
                out[categoryKey] = filtered;
            }
        });
        return out;
    }, [
        menuItems,
        menuTypeFilter,
        menuSourceFilter,
        foodSearchQuery,
        foodTypeFilter,
        foodCategoryFilter,
        statusFilter,
    ]);

    const displayMenuItemsFlat = useMemo(() => {
        const rows = [];
        Object.keys(displayMenuItems).forEach((categoryKey) => {
            const arr = displayMenuItems[categoryKey];
            if (!Array.isArray(arr)) return;
            arr.forEach((item) => {
                rows.push({
                    item,
                    category: getMenuItemFoodCategory(item, categoryKey),
                });
            });
        });
        return rows;
    }, [displayMenuItems]);

    const sortedMenuItemsFlat = useMemo(() => {
        if (!menuSort?.key) return displayMenuItemsFlat;
        return [...displayMenuItemsFlat].sort((a, b) =>
            compareMenuRows(a, b, menuSort.key, menuSort.direction)
        );
    }, [displayMenuItemsFlat, menuSort]);

    useEffect(() => {
        setMenuPage(1);
    }, [foodSearchQuery, foodTypeFilter, foodCategoryFilter, statusFilter, menuTypeFilter, menuSourceFilter]);

    useEffect(() => {
        setMenuSort(null);
        setFoodCategoryFilter('');
        setFoodTypeFilter('');
        setStatusFilter('');
    }, [menuTypeFilter]);

    const handleMenuSortAsc = (key) => {
        setMenuPage(1);
        setMenuSort((prev) => {
            if (prev?.key === key && prev.direction === 'asc') {
                return null;
            }
            return { key, direction: 'asc' };
        });
    };

    const handleMenuSortDesc = (key) => {
        setMenuPage(1);
        setMenuSort((prev) => {
            if (prev?.key === key && prev.direction === 'desc') {
                return null;
            }
            return { key, direction: 'desc' };
        });
    };

    const menuTotalPages = Math.max(
        1,
        Math.ceil(sortedMenuItemsFlat.length / menuPageSize)
    );
    const menuPageSafe = Math.min(Math.max(1, menuPage), menuTotalPages);

    useEffect(() => {
        setMenuPage((p) => Math.min(p, menuTotalPages));
    }, [menuTotalPages]);

    const menuPageStart = (menuPageSafe - 1) * menuPageSize;
    const paginatedMenuItemsFlat = sortedMenuItemsFlat.slice(
        menuPageStart,
        menuPageStart + menuPageSize
    );

    const handleMenuPageChange = (nextPage) => {
        setMenuPage(nextPage);
    };

    const handleMenuPageSizeChange = (nextSize) => {
        setMenuPageSize(nextSize);
        setMenuPage(1);
    };

    const hasActiveMenuFilters = Boolean(foodTypeFilter || foodCategoryFilter || statusFilter);

    const handleRecipeAction = async (foodItemId) => {
        const id = Number(foodItemId);
        if (!id || typeof onRecipeNavigate !== 'function') return;

        const cached = recipeExistsByItemId[String(id)];
        if (cached === true) {
            onRecipeNavigate(id, 'view');
            return;
        }
        if (cached === false) {
            onRecipeNavigate(id, 'add');
            return;
        }

        try {
            const data = await fetchFoodItemRecipe(
                menuTypeFilter === 'catering'
                    ? { cateringItemId: id }
                    : { foodItemId: id }
            );
            if (recipeResponseHasContent(data)) {
                setRecipeExistsForItem(id, true);
                onRecipeNavigate(id, 'view');
            } else {
                setRecipeExistsForItem(id, false);
                onRecipeNavigate(id, 'add');
            }
        } catch {
            setRecipeExistsForItem(id, false);
            onRecipeNavigate(id, 'add');
        }
    };

    const selectedIngredientIds = Array.isArray(newMenuItem.ingredientIds)
        ? newMenuItem.ingredientIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
        : [];

    useEffect(() => {
        if (menuTypeFilter !== 'online') {
            setIngredientOptions([]);
            return;
        }
        const fetchIngredients = async () => {
            try {
                setIngredientsLoading(true);
                const response = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.restaurantsIngredients);
                const list = response.data?.ingredients;
                setIngredientOptions(normalizeIngredientOptions(list));
            } catch (error) {
                console.error('Error fetching ingredients for dropdown:', error);
                setIngredientOptions([]);
            } finally {
                setIngredientsLoading(false);
            }
        };

        fetchIngredients();
    }, [menuTypeFilter]);

    const handleIngredientSelectionChange = (nextSelectedIds) => {
        const normalizedIds = (Array.isArray(nextSelectedIds) ? nextSelectedIds : [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id));
        const nextSelectedNames = ingredientOptions
            .filter((item) => normalizedIds.includes(Number(item.ingredientId)))
            .map((item) => item.ingredientName);
        handleInputChange({
            target: {
                type: 'ingredient-select',
                name: 'ingredientIds',
                value: normalizedIds,
                ingredients: nextSelectedNames.join(', '),
            },
        });
    };

    const foodCategoryOptions = useMemo(() => {
        const categories = Array.isArray(foodCategories) ? foodCategories : [];
        return categories
            .map((category) => {
                const name = extractFoodCategoryName(category);
                if (!name) return null;
                if (isCateringMenu) {
                    const id = extractFoodCategoryId(category);
                    return id != null ? { value: String(id), label: name } : null;
                }
                return { value: name, label: name };
            })
            .filter(Boolean);
    }, [foodCategories, isCateringMenu]);

    const foodCategoryFilterOptions = useMemo(() => {
        if (foodCategoryOptions.length > 0) return foodCategoryOptions;
        const keys = Object.keys(menuItems || {}).filter(
            (k) => Array.isArray(menuItems[k]) && menuItems[k].length > 0
        );
        return keys.sort().map((k) => ({ value: k, label: k }));
    }, [foodCategoryOptions, menuItems]);

    const menuCategoryFilterRichOptions = useMemo(
        () => buildMenuCategoryFilterOptions(foodCategoryFilterOptions),
        [foodCategoryFilterOptions]
    );

    const handleFoodCategoryFilterChange = (value) => {
        setMenuPage(1);
        setFoodCategoryFilter(value);
    };

    const handleFoodTypeFilterChange = (value) => {
        setMenuPage(1);
        setFoodTypeFilter(value);
    };

    const handleStatusFilterChange = (value) => {
        setMenuPage(1);
        setStatusFilter(value);
    };

    const [editDraft, setEditDraft] = useState(null);
    const [showEditStatusField, setShowEditStatusField] = useState(false);
    const [editImagePreviewUrl, setEditImagePreviewUrl] = useState('');
    const [addImagePreviewUrl, setAddImagePreviewUrl] = useState('');
    const menuImageFileInputRef = useRef(null);

    useEffect(() => {
        const file = newMenuItem?.imageFile ?? null;
        if (!file) {
            setAddImagePreviewUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return '';
            });
            return undefined;
        }
        const url = URL.createObjectURL(file);
        setAddImagePreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return url;
        });
        return () => URL.revokeObjectURL(url);
    }, [newMenuItem?.imageFile]);

    useEffect(() => {
        if (!editingItem) {
            setEditDraft(null);
            setShowEditStatusField(false);
            setEditImagePreviewUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return '';
            });
            return;
        }

        setShowEditStatusField(
            !isMenuItemActiveStatus(editingItem.Status ?? editingItem.status)
        );

        setEditDraft({
            Food_Item_Id: editingItem.Food_Item_Id,
            Master_Food_Item_Id:
                editingItem.Master_Food_Item_Id ?? editingItem.masterFoodItemId ?? null,
            Food_Item: editingItem.Food_Item ?? '',
            foodCategoryId: editingItem.foodCategoryId ?? editingItem.Food_Category_Id ?? '',
            foodCategory: editingItem.foodCategory ?? editingItem.Food_Category ?? editingItem.Food_Type ?? '',
            Food_Type: editingItem.foodCategory ?? editingItem.Food_Category ?? editingItem.Food_Type ?? '',
            itemFoodType: editingItem.itemFoodType ?? editingItem.foodType ?? 'VEG',
            Price: editingItem.Price ?? '',
            Quantity: editingItem.Quantity ?? '',
            nutrition: parseNutritionRowsFromItem(editingItem),
            Menu_Source: editingItem.Menu_Source ?? editingItem.menuSource ?? 'MYEATS',
            menuSource: editingItem.Menu_Source ?? editingItem.menuSource ?? 'MYEATS',
            availableForDineIn: resolveChannelFlag(
                editingItem,
                ['availableForDineIn', 'available_for_dine_in', 'Available_For_Dine_In'],
                (editingItem.Menu_Source ?? editingItem.menuSource ?? 'MYEATS') !== 'JREATS'
            ),
            availableForOnline: resolveChannelFlag(
                editingItem,
                ['availableForOnline', 'available_for_online', 'Available_For_Online'],
                true
            ),
            Description: editingItem.Description ?? editingItem.description ?? '',
            description: editingItem.Description ?? editingItem.description ?? '',
            cuisineOrigin: editingItem.cuisineOrigin ?? '',
            ...(isCateringMenu ? {} : { Notes: editingItem.Notes ?? '' }),
            ...(menuTypeFilter !== 'catering'
                ? {
                      Ingredients:
                          typeof editingItem.Ingredients === 'string'
                              ? editingItem.Ingredients
                              : formatOnlineIngredientsDisplay(editingItem),
                  }
                : {}),
            Status: editingItem.Status ?? editingItem.status ?? 'ACTIVE',
            status: editingItem.Status ?? editingItem.status ?? 'ACTIVE',
            isAvailable: isCateringItemAvailable(editingItem),
            ingredientIds: resolveIngredientIdsFromMenuItem(editingItem),
            Currency: editingItem.Currency ?? 'INR',
            imageFile: null,
            agePricing: parseAgePricingFromItem(editingItem),
            ...mapGroupScaleFromApi(editingItem),
        });
        setEditImagePreviewUrl('');
    }, [editingItem]);

    const closeAddModal = (e) => {
        e?.preventDefault?.();
        handleCloseAddMenu(e || { preventDefault: () => {} });
    };

    const closeEditModal = () => {
        handleCancelEdit();
    };

    const updateEditDraft = (patch) => {
        setEditDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    };

    const handleEditImageFileChange = (file) => {
        setEditImagePreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return file ? URL.createObjectURL(file) : '';
        });
        updateEditDraft({ imageFile: file ?? null });
    };

    const handleMenuImageInputChange = async (e, isAdd) => {
        const rawFile = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (!rawFile) {
            if (isAdd) {
                handleInputChange({ target: { type: 'file', files: [] } });
            } else {
                handleEditImageFileChange(null);
            }
            return;
        }
        try {
            const processedFile = await processMenuImageFile(rawFile);
            if (isAdd) {
                handleInputChange({ target: { type: 'file', files: [processedFile] } });
            } else {
                handleEditImageFileChange(processedFile);
            }
        } catch (error) {
            alert(error?.message || 'Failed to process image.');
        }
    };

    const submitEdit = (e) => {
        e?.preventDefault?.();
        if (!editDraft) return;
        const masterLinked = Boolean(editDraft.Master_Food_Item_Id);
        const payload = {
            ...editDraft,
            Food_Item_Id: editDraft.Food_Item_Id,
            restaurantId,
            foodCategoryId: editDraft.foodCategoryId,
            foodCategory: editDraft.foodCategory,
            foodType: isCateringMenu
                ? (editDraft.itemFoodType ?? 'VEG')
                : (editDraft.foodCategory ?? editDraft.Food_Type),
            itemFoodType: editDraft.itemFoodType ?? 'VEG',
            foodItem: editDraft.Food_Item,
            price: editDraft.Price,
            quantity: editDraft.Quantity,
            description: editDraft.Description ?? editDraft.description,
            nutrition: editDraft.nutrition,
            menuSource:
                editDraft.Menu_Source ?? editDraft.menuSource ?? 'MYEATS',
            status: editDraft.Status ?? editDraft.status ?? 'ACTIVE',
            cuisineOrigin: editDraft.cuisineOrigin,
            currency: editDraft.Currency ?? 'INR',
            notes: isCateringMenu ? undefined : editDraft.Notes,
            ...(menuTypeFilter !== 'catering'
                ? {
                      ingredients:
                          typeof editDraft.Ingredients === 'string'
                              ? editDraft.Ingredients
                              : undefined,
                  }
                : {}),
            isAvailable: editDraft.isAvailable ? '1' : '0',
            ingredientIds: Array.isArray(editDraft.ingredientIds)
                ? editDraft.ingredientIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
                : resolveIngredientIdsFromMenuItem(editDraft),
            imageFile: editDraft.imageFile,
            agePricing: normalizeAgePricingForSubmit(editDraft.agePricing),
            groupScaleEnabled: Boolean(editDraft.groupScaleEnabled),
            servingUnit: editDraft.servingUnit,
            basePortionQty: editDraft.basePortionQty,
            peoplePerPortion: editDraft.peoplePerPortion,
            qtyStep: editDraft.qtyStep,
            minOrderQty: editDraft.minOrderQty,
            courseRole: editDraft.courseRole,
            partyUnitPrice: editDraft.partyUnitPrice,
        };
        if (masterLinked) {
            // Avoid sending master-owned fields (API rejects them on linked items).
            delete payload.foodCategory;
            delete payload.foodCategoryId;
            delete payload.foodType;
            delete payload.itemFoodType;
            delete payload.foodItem;
            delete payload.description;
            delete payload.cuisineOrigin;
            delete payload.ingredientIds;
            delete payload.ingredients;
            delete payload.nutrition;
            delete payload.imageFile;
            delete payload.courseRole;
            delete payload.Food_Item;
            delete payload.Food_Type;
            delete payload.Description;
        }
        handleUpdateItem(payload);
    };

    const editDraftSelectedIds = Array.isArray(editDraft?.ingredientIds)
        ? editDraft.ingredientIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
        : [];

    const renderMenuItemFormFields = (formMode) => {
        const isAdd = formMode === 'add';
        const values = isAdd ? newMenuItem : editDraft;
        if (!values) return null;

        const masterLocked = !isAdd && Boolean(values.Master_Food_Item_Id);

        const editImageSrc =
            editImagePreviewUrl ||
            (editingItem ? getDisplayImageUrl(editingItem) : '') ||
            defaultFoodImage;

        const modalImagePreviewSrc = isAdd ? addImagePreviewUrl : editImageSrc;
        const modalImageAlt = isAdd
            ? (values.foodItem ?? 'Menu item')
            : (values.Food_Item ?? 'Menu item');

        const selectedImageFileName = isAdd
            ? (newMenuItem.imageFile?.name ?? '')
            : (editDraft?.imageFile?.name ?? '');

        const menuSourceValue = String(
            isAdd
                ? values.menuSource || 'MYEATS'
                : values.Menu_Source ?? values.menuSource ?? 'MYEATS'
        ).toUpperCase();
        const showAgePricingEditor = isOnlineMenu && menuSourceValue === 'JREATS';
        const showNutritionEditor =
            isOnlineMenu && (menuSourceValue === 'JREATS' || menuSourceValue === 'MYEATS');
        const showGroupScaleEditor = isOnlineMenu && menuSourceValue === 'MYEATS';

        const renderModalImageField = () =>
            showImageColumn ? (
                <div className="inventory-form-group menu-item-modal-image-group">
                    <label>Image</label>
                    <div className="menu-item-modal-image-field">
                        {modalImagePreviewSrc ? (
                            <img
                                src={modalImagePreviewSrc}
                                alt={modalImageAlt}
                                className="menu-item-modal-image-preview"
                                onError={(ev) => {
                                    ev.target.onerror = null;
                                    ev.target.src = defaultFoodImage;
                                }}
                            />
                        ) : null}
                        <button
                            type="button"
                            className="menu-item-modal-file-btn"
                            disabled={masterLocked}
                            onClick={() => {
                                if (masterLocked) return;
                                menuImageFileInputRef.current?.click();
                            }}
                        >
                            Choose File
                        </button>
                        <span className="menu-item-modal-file-name">
                            {selectedImageFileName || 'No file chosen'}
                        </span>
                        <input
                            ref={menuImageFileInputRef}
                            type="file"
                            accept="image/*"
                            className="menu-item-modal-file-input-hidden"
                            aria-label="Choose image file"
                            tabIndex={-1}
                            disabled={masterLocked}
                            onChange={(e) => {
                                if (masterLocked) return;
                                handleMenuImageInputChange(e, isAdd);
                            }}
                        />
                    </div>
                </div>
            ) : null;

        const foodCategoryValue = isCateringMenu
            ? String(
                  isAdd
                      ? (values.foodCategoryId ?? '')
                      : (values.foodCategoryId ?? values.foodCategory ?? values.Food_Type ?? '')
              )
            : isAdd
              ? (values.foodType ?? '')
              : (values.foodCategory ?? values.Food_Type ?? '');

        return (
            <div className="menu-item-modal-form">
                {masterLocked ? (
                    <p className="menu-template-jreats-state-hint menu-item-modal-form-span-2">
                        Linked to global menu master — name, category, diet, course, description,
                        ingredients, nutrition, and image are managed in Eats Admin. You can change
                        price, quantity, availability, and status.
                    </p>
                ) : null}
                {isOnlineMenu ? (
                    <>
                        {/* Row 1: Food Category | Food Item Name */}
                        <div className="inventory-form-group">
                            <label className="menu-form-label--required">Food category</label>
                            <SearchableSelect
                                className="add-menu-form-food-type-select"
                                triggerClassName="add-menu-form-field add-menu-form-field-food-type inventory-select-trigger"
                                value={foodCategoryValue}
                                disabled={masterLocked}
                                onChange={(foodType) => {
                                    if (masterLocked) return;
                                    if (isAdd) {
                                        handleInputChange({
                                            target: {
                                                name: isCateringMenu ? 'foodCategoryId' : 'foodType',
                                                value: foodType ?? '',
                                            },
                                        });
                                    } else {
                                        const selected = foodCategoryOptions.find(
                                            (opt) => opt.value === String(foodType ?? '')
                                        );
                                        updateEditDraft({
                                            foodCategoryId: isCateringMenu ? (foodType ?? '') : undefined,
                                            foodCategory: isCateringMenu
                                                ? (selected?.label ?? foodType ?? '')
                                                : (foodType ?? ''),
                                            Food_Type: isCateringMenu
                                                ? (selected?.label ?? foodType ?? '')
                                                : (foodType ?? ''),
                                        });
                                    }
                                }}
                                options={foodCategoryOptions}
                                placeholder="Select food category"
                                searchPlaceholder="Search categories"
                                emptyMessage="No categories found"
                                dropdownMinWidth={240}
                                minOptionsForSearch={1}
                            />
                        </div>
                        <div className="inventory-form-group">
                            <label className="menu-form-label--required">Food item name</label>
                            <input
                                type="text"
                                className="add-menu-form-field"
                                name="foodItem"
                                placeholder="Food item name"
                                disabled={masterLocked}
                                value={isAdd ? (values.foodItem ?? '') : (values.Food_Item ?? '')}
                                onChange={(e) => {
                                    if (masterLocked) return;
                                    if (isAdd) {
                                        handleInputChange(e);
                                    } else {
                                        updateEditDraft({ Food_Item: e.target.value });
                                    }
                                }}
                            />
                        </div>

                        {/* Row 2: Food Type | Price */}
                        <div className="inventory-form-group">
                            <label>Food type</label>
                            <div className="inventory-native-select-wrap">
                                <select
                                    className="add-menu-form-field inventory-select-native"
                                    name="itemFoodType"
                                    disabled={masterLocked}
                                    value={normalizeDietType(values.itemFoodType) || 'VEG'}
                                    onChange={(e) => {
                                        if (masterLocked) return;
                                        if (isAdd) {
                                            handleInputChange(e);
                                        } else {
                                            updateEditDraft({ itemFoodType: e.target.value });
                                        }
                                    }}
                                >
                                    <option value="VEG">Veg</option>
                                    <option value="NON-VEG">Non-Veg</option>
                                </select>
                            </div>
                        </div>
                        <div className="inventory-form-group">
                            <label className="menu-form-label--required">Price</label>
                            <div className="menu-item-modal-price-row">
                                <div className="inventory-native-select-wrap menu-item-modal-currency-wrap">
                                    <select
                                        className="add-menu-form-field inventory-select-native menu-item-modal-currency-select"
                                        name="currency"
                                        value={
                                            isAdd
                                                ? (values.currency ?? 'INR')
                                                : (values.Currency ?? values.currency ?? 'INR')
                                        }
                                        onChange={(e) => {
                                            if (isAdd) {
                                                handleInputChange(e);
                                            } else {
                                                updateEditDraft({
                                                    Currency: e.target.value,
                                                    currency: e.target.value,
                                                });
                                            }
                                        }}
                                    >
                                        <option value="INR">₹</option>
                                        <option value="USD">$</option>
                                    </select>
                                </div>
                                <input
                                    type="number"
                                    className="add-menu-form-field menu-item-modal-price-input"
                                    name="price"
                                    placeholder="Price"
                                    value={isAdd ? (values.price ?? '') : (values.Price ?? '')}
                                    onChange={(e) => {
                                        if (isAdd) {
                                            handleInputChange(e);
                                        } else {
                                            updateEditDraft({ Price: e.target.value });
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        {/* Row 3: Quantity | Cuisine Origin */}
                        <div className="inventory-form-group">
                            <label className="menu-form-label--required">Quantity</label>
                            <input
                                type="text"
                                className="add-menu-form-field"
                                name="quantity"
                                placeholder="Quantity"
                                required
                                value={isAdd ? (values.quantity ?? '') : (values.Quantity ?? '')}
                                onChange={(e) => {
                                    if (isAdd) {
                                        handleInputChange(e);
                                    } else {
                                        updateEditDraft({ Quantity: e.target.value });
                                    }
                                }}
                            />
                        </div>

                        {showGroupScaleEditor ? (
                            <div className="inventory-form-group menu-item-modal-form-span-2">
                                <label>
                                    <input
                                        type="checkbox"
                                        name="groupScaleEnabled"
                                        checked={Boolean(values.groupScaleEnabled)}
                                        onChange={(e) => {
                                            if (isAdd) {
                                                handleInputChange(e);
                                            } else {
                                                updateEditDraft({
                                                    groupScaleEnabled: e.target.checked,
                                                });
                                            }
                                        }}
                                    />{' '}
                                    Enable group-order scaling
                                </label>
                                {values.groupScaleEnabled ? (
                                    <div className="menu-item-modal-form menu-item-group-scale-grid">
                                        <div className="inventory-form-group">
                                            <label>Serving unit</label>
                                            <div className="inventory-native-select-wrap">
                                                <select
                                                    className="add-menu-form-field inventory-select-native"
                                                    name="servingUnit"
                                                    value={values.servingUnit || ''}
                                                    onChange={(e) => {
                                                        if (isAdd) {
                                                            handleInputChange(e);
                                                        } else {
                                                            updateEditDraft({
                                                                servingUnit: e.target.value,
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <option value="">Select unit</option>
                                                    {GROUP_SERVING_UNITS.map((unit) => (
                                                        <option key={unit.value} value={unit.value}>
                                                            {unit.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="inventory-form-group">
                                            <label>Course</label>
                                            <div className="inventory-native-select-wrap">
                                                <select
                                                    className="add-menu-form-field inventory-select-native"
                                                    name="courseRole"
                                                    value={values.courseRole || ''}
                                                    onChange={(e) => {
                                                        if (isAdd) {
                                                            handleInputChange(e);
                                                        } else {
                                                            updateEditDraft({
                                                                courseRole: e.target.value,
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <option value="">Select course</option>
                                                    {GROUP_COURSE_ROLES.map((role) => (
                                                        <option key={role.value} value={role.value}>
                                                            {role.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        {[
                                            ['basePortionQty', 'Base portion qty'],
                                            ['peoplePerPortion', 'People per portion'],
                                            ['qtyStep', 'Quantity step'],
                                            ['minOrderQty', 'Min order qty'],
                                            [
                                                'partyUnitPrice',
                                                'Party unit price (optional, ₹ per serving unit)',
                                            ],
                                        ].map(([name, label]) => (
                                            <div className="inventory-form-group" key={name}>
                                                <label>{label}</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    className="add-menu-form-field"
                                                    name={name}
                                                    placeholder={
                                                        name === 'partyUnitPrice'
                                                            ? 'e.g. 400 for ₹/kg'
                                                            : label
                                                    }
                                                    value={values[name] ?? ''}
                                                    onChange={(e) => {
                                                        if (isAdd) {
                                                            handleInputChange(e);
                                                        } else {
                                                            updateEditDraft({
                                                                [name]: e.target.value,
                                                            });
                                                        }
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        <div className="inventory-form-group">
                            <label>Cuisine origin</label>
                            <input
                                type="text"
                                className="add-menu-form-field"
                                name="cuisineOrigin"
                                placeholder="Cuisine origin"
                                value={values.cuisineOrigin ?? ''}
                                onChange={(e) => {
                                    if (isAdd) {
                                        handleInputChange(e);
                                    } else {
                                        updateEditDraft({ cuisineOrigin: e.target.value });
                                    }
                                }}
                            />
                        </div>

                        {/* Row 3b: Menu source | Sell on */}
                        <div className="inventory-form-group">
                            <label>Menu source</label>
                            <div className="inventory-native-select-wrap">
                                <select
                                    className="add-menu-form-field inventory-select-native"
                                    name="menuSource"
                                    value={String(
                                        isAdd
                                            ? values.menuSource || 'MYEATS'
                                            : values.Menu_Source ?? values.menuSource ?? 'MYEATS'
                                    ).toUpperCase()}
                                    onChange={(e) => {
                                        const nextSource = e.target.value;
                                        const nextFlags =
                                            nextSource === 'JREATS'
                                                ? {
                                                      availableForDineIn: false,
                                                      availableForOnline: true,
                                                  }
                                                : {
                                                      availableForDineIn: true,
                                                      availableForOnline: true,
                                                  };
                                        if (isAdd) {
                                            handleInputChange(e);
                                            handleInputChange({
                                                target: {
                                                    name: 'availableForDineIn',
                                                    checked: nextFlags.availableForDineIn,
                                                    type: 'checkbox',
                                                },
                                            });
                                            handleInputChange({
                                                target: {
                                                    name: 'availableForOnline',
                                                    checked: nextFlags.availableForOnline,
                                                    type: 'checkbox',
                                                },
                                            });
                                        } else {
                                            updateEditDraft({
                                                Menu_Source: nextSource,
                                                menuSource: nextSource,
                                                ...nextFlags,
                                            });
                                        }
                                    }}
                                >
                                    <option value="MYEATS">MYEATS</option>
                                    <option value="JREATS">JREATS</option>
                                </select>
                            </div>
                        </div>
                        {menuSourceValue === 'MYEATS' ? (
                            <div className="inventory-form-group">
                                <label>Sell on</label>
                                <div className="menu-channel-flag-checkboxes">
                                    <label className="menu-channel-flag-option">
                                        <input
                                            type="checkbox"
                                            checked={resolveChannelFlag(
                                                values,
                                                ['availableForDineIn', 'available_for_dine_in', 'Available_For_Dine_In'],
                                                menuSourceValue !== 'JREATS'
                                            )}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                if (isAdd) {
                                                    handleInputChange({
                                                        target: {
                                                            name: 'availableForDineIn',
                                                            checked,
                                                            type: 'checkbox',
                                                        },
                                                    });
                                                } else {
                                                    updateEditDraft({
                                                        availableForDineIn: checked,
                                                    });
                                                }
                                            }}
                                        />
                                        Dine-In
                                    </label>
                                    <label className="menu-channel-flag-option">
                                        <input
                                            type="checkbox"
                                            checked={resolveChannelFlag(
                                                values,
                                                ['availableForOnline', 'available_for_online', 'Available_For_Online'],
                                                true
                                            )}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                if (isAdd) {
                                                    handleInputChange({
                                                        target: {
                                                            name: 'availableForOnline',
                                                            checked,
                                                            type: 'checkbox',
                                                        },
                                                    });
                                                } else {
                                                    updateEditDraft({
                                                        availableForOnline: checked,
                                                    });
                                                }
                                            }}
                                        />
                                        My Eats (online)
                                    </label>
                                </div>
                            </div>
                        ) : null}
                        <div className="inventory-form-group">
                            <label>Ingredients</label>
                            <IngredientsMultiSelect
                                className="ingredients-dropdown add-menu-form-ingredients"
                                options={ingredientOptions}
                                selectedIds={
                                    isAdd ? selectedIngredientIds : editDraftSelectedIds
                                }
                                onChange={
                                    isAdd
                                        ? handleIngredientSelectionChange
                                        : (nextIds) =>
                                              updateEditDraft({
                                                  ingredientIds: (
                                                      Array.isArray(nextIds) ? nextIds : []
                                                  )
                                                      .map((id) => Number(id))
                                                      .filter((id) => Number.isFinite(id)),
                                              })
                                }
                                loading={ingredientsLoading}
                            />
                        </div>

                        {showAgePricingEditor ? (
                            <AgePricingEditor
                                value={isAdd ? (values.agePricing ?? []) : (values.agePricing ?? [])}
                                onChange={(rows) => {
                                    if (isAdd) {
                                        handleInputChange({
                                            target: { name: 'agePricing', value: rows },
                                        });
                                    } else {
                                        updateEditDraft({ agePricing: rows });
                                    }
                                }}
                            />
                        ) : null}

                        {showNutritionEditor ? (
                            <NutritionEditor
                                value={isAdd ? (values.nutrition ?? []) : (values.nutrition ?? [])}
                                onChange={(rows) => {
                                    if (isAdd) {
                                        handleInputChange({
                                            target: { name: 'nutrition', value: rows },
                                        });
                                    } else {
                                        updateEditDraft({ nutrition: rows });
                                    }
                                }}
                            />
                        ) : null}

                        {/* Row 5: Image | Description */}
                        {renderModalImageField()}
                        <div className="inventory-form-group">
                            <label>Description</label>
                            <textarea
                                type="text"
                                className="add-menu-form-field"
                                name="description"
                                placeholder="Description"
                                value={
                                    isAdd
                                        ? (values.description ?? '')
                                        : (values.Description ?? values.description ?? '')
                                }
                                onChange={(e) => {
                                    if (isAdd) {
                                        handleInputChange(e);
                                    } else {
                                        updateEditDraft({
                                            Description: e.target.value,
                                            description: e.target.value,
                                        });
                                    }
                                }}
                            />
                        </div>

                        {!isAdd && showEditStatusField && (
                            <div className="inventory-form-group">
                                <label>Status</label>
                                <div className="inventory-native-select-wrap">
                                    <select
                                        className="add-menu-form-field inventory-select-native"
                                        value={String(values.Status ?? values.status ?? 'ACTIVE').toUpperCase()}
                                        onChange={(e) => {
                                            updateEditDraft({
                                                Status: e.target.value,
                                                status: e.target.value,
                                            });
                                        }}
                                    >
                                        <option value="ACTIVE">Active</option>
                                        <option value="INACTIVE">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {/* Catering / offline menu */}
                        <div className="inventory-form-group">
                            <label className="menu-form-label--required">Food category</label>
                            <SearchableSelect
                                className="add-menu-form-food-type-select"
                                triggerClassName="add-menu-form-field add-menu-form-field-food-type inventory-select-trigger"
                                value={foodCategoryValue}
                                onChange={(foodType) => {
                                    if (isAdd) {
                                        handleInputChange({
                                            target: {
                                                name: isCateringMenu ? 'foodCategoryId' : 'foodType',
                                                value: foodType ?? '',
                                            },
                                        });
                                    } else {
                                        const selected = foodCategoryOptions.find(
                                            (opt) => opt.value === String(foodType ?? '')
                                        );
                                        updateEditDraft({
                                            foodCategoryId: isCateringMenu ? (foodType ?? '') : undefined,
                                            foodCategory: isCateringMenu
                                                ? (selected?.label ?? foodType ?? '')
                                                : (foodType ?? ''),
                                            Food_Type: isCateringMenu
                                                ? (selected?.label ?? foodType ?? '')
                                                : (foodType ?? ''),
                                        });
                                    }
                                }}
                                options={foodCategoryOptions}
                                placeholder="Select food category"
                                searchPlaceholder="Search categories"
                                emptyMessage="No categories found"
                                dropdownMinWidth={240}
                                minOptionsForSearch={1}
                            />
                        </div>
                        <div className="inventory-form-group">
                            <label className="menu-form-label--required">Food item name</label>
                            <input
                                type="text"
                                className="add-menu-form-field"
                                name="foodItem"
                                placeholder="Food item name"
                                value={isAdd ? (values.foodItem ?? '') : (values.Food_Item ?? '')}
                                onChange={(e) => {
                                    if (isAdd) {
                                        handleInputChange(e);
                                    } else {
                                        updateEditDraft({ Food_Item: e.target.value });
                                    }
                                }}
                            />
                        </div>
                        <div className="inventory-form-group">
                            <label className="menu-form-label--required">Price</label>
                            <div className="menu-item-modal-price-row">
                                <div className="inventory-native-select-wrap menu-item-modal-currency-wrap">
                                    <select
                                        className="add-menu-form-field inventory-select-native menu-item-modal-currency-select"
                                        name="currency"
                                        value={
                                            isAdd
                                                ? (values.currency ?? 'INR')
                                                : (values.Currency ?? values.currency ?? 'INR')
                                        }
                                        onChange={(e) => {
                                            if (isAdd) {
                                                handleInputChange(e);
                                            } else {
                                                updateEditDraft({
                                                    Currency: e.target.value,
                                                    currency: e.target.value,
                                                });
                                            }
                                        }}
                                    >
                                        <option value="INR">₹</option>
                                        <option value="USD">$</option>
                                    </select>
                                </div>
                                <input
                                    type="number"
                                    className="add-menu-form-field menu-item-modal-price-input"
                                    name="price"
                                    placeholder="Price"
                                    value={isAdd ? (values.price ?? '') : (values.Price ?? '')}
                                    onChange={(e) => {
                                        if (isAdd) {
                                            handleInputChange(e);
                                        } else {
                                            updateEditDraft({ Price: e.target.value });
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div className="inventory-form-group">
                            <label className="menu-form-label--required">Quantity</label>
                            <input
                                type="text"
                                className="add-menu-form-field"
                                name="quantity"
                                placeholder="Quantity"
                                required
                                value={isAdd ? (values.quantity ?? '') : (values.Quantity ?? '')}
                                onChange={(e) => {
                                    if (isAdd) {
                                        handleInputChange(e);
                                    } else {
                                        updateEditDraft({ Quantity: e.target.value });
                                    }
                                }}
                            />
                        </div>
                        <div className="inventory-form-group">
                            <label>Food type</label>
                            <div className="inventory-native-select-wrap">
                                <select
                                    className="add-menu-form-field inventory-select-native"
                                    name="itemFoodType"
                                    value={normalizeDietType(values.itemFoodType) || 'VEG'}
                                    onChange={(e) => {
                                        if (isAdd) {
                                            handleInputChange(e);
                                        } else {
                                            updateEditDraft({ itemFoodType: e.target.value });
                                        }
                                    }}
                                >
                                    <option value="VEG">Veg</option>
                                    <option value="NON-VEG">Non-Veg</option>
                                </select>
                            </div>
                        </div>
                        {isCateringMenu && (
                            <div className="inventory-form-group">
                                <label>Available</label>
                                <div className="inventory-native-select-wrap">
                                    <select
                                        className="add-menu-form-field inventory-select-native"
                                        name="isAvailable"
                                        value={
                                            isAdd
                                                ? values.isAvailable === false || values.isAvailable === '0'
                                                    ? '0'
                                                    : '1'
                                                : values.isAvailable
                                                  ? '1'
                                                  : '0'
                                        }
                                        onChange={(e) => {
                                            if (isAdd) {
                                                handleInputChange(e);
                                            } else {
                                                updateEditDraft({ isAvailable: e.target.value === '1' });
                                            }
                                        }}
                                    >
                                        <option value="1">Yes</option>
                                        <option value="0">No</option>
                                    </select>
                                </div>
                            </div>
                        )}
                        <div className="inventory-form-group">
                            <label>Description</label>
                            <textarea
                                className="add-menu-form-field"
                                name="description"
                                placeholder="Description"
                                value={
                                    isAdd
                                        ? (values.description ?? '')
                                        : (values.Description ?? values.description ?? '')
                                }
                                onChange={(e) => {
                                    if (isAdd) {
                                        handleInputChange(e);
                                    } else {
                                        updateEditDraft({
                                            Description: e.target.value,
                                            description: e.target.value,
                                        });
                                    }
                                }}
                            />
                        </div>
                        {renderModalImageField()}
                    </>
                )}
            </div>
        );
    };

    const renderMenuItemModal = ({ open, title, titleId, onClose, onSubmit, submitLabel, formMode }) => {
        if (!open) return null;
        return (
            <div
                className="inventory-modal-overlay menu-item-modal-overlay"
                role="presentation"
            >
                <div
                    className="inventory-modal menu-item-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                >
                    <div className="inventory-modal-header menu-item-modal-header">
                        <h4 id={titleId}>{title}</h4>
                    </div>
                    <form
                        className="menu-item-modal-form-wrap"
                        onSubmit={onSubmit}
                    >
                        {renderMenuItemFormFields(formMode)}
                        <div className="inventory-form-actions menu-template-modal-actions">
                            <button type="submit" className="add-button">
                                {submitLabel}
                            </button>
                            <button type="button" className="close-button" onClick={onClose}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    // Clean URL (strip trailing %22 / quotes) – same for online and catering
    const cleanImageUrl = (url) => {
        if (url == null || typeof url !== 'string') return '';
        return String(url).trim().replace(/^%22+|%22+$/g, '').replace(/^"+|"+$/g, '').trim();
    };
    // Build image URL: full URL as-is, else origin + /uploads/ + path (same as online)
    const getOfflineImageUrl = (imagePath) => {
        if (!imagePath) return '';
        const path = String(imagePath).trim().replace(/^\//, '');
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return cleanImageUrl(path);
        const base = axiosRestaurantInstance.defaults.baseURL || process.env.REACT_APP_API_COMPANY_URL || '';
        if (!base) return path.startsWith('http') ? path : `/${path}`;
        try {
            const origin = new URL(base).origin;
            const pathWithoutUploads = path.startsWith('uploads/') ? path : `uploads/${path}`;
            return `${origin}/${pathWithoutUploads}`;
        } catch (e) {
            const trimmed = base.replace(/\/$/, '');
            const pathWithoutUploads = path.startsWith('uploads/') ? path : `uploads/${path}`;
            return `${trimmed}/${pathWithoutUploads}`;
        }
    };
    // Use imageUrl from API when present (online/catering), else build from imagePath – same for both
    const getDisplayImageUrl = (item) => {
        if (!item) return '';
        const { imageUrl, imagePath } = resolveFoodItemImageFields(item);
        const builtFromPath = imagePath ? getOfflineImageUrl(imagePath) : '';

        if (imageUrl) {
            const cleaned = cleanImageUrl(imageUrl);
            if (builtFromPath) {
                try {
                    const apiBase =
                        axiosRestaurantInstance.defaults.baseURL ||
                        process.env.REACT_APP_API_COMPANY_URL ||
                        '';
                    if (apiBase) {
                        const apiOrigin = new URL(apiBase).origin;
                        const urlOrigin = new URL(cleaned).origin;
                        if (apiOrigin && urlOrigin !== apiOrigin) {
                            return builtFromPath;
                        }
                    }
                } catch (e) {
                    // fall through to cleaned API URL
                }
            }
            return cleaned;
        }

        return builtFromPath;
    };

    // Fetch menu items (Online: /food-items or JREATS catalog, Catering: /catering-menu-items)
    const fetchMenuItems = async () => {
        if (!restaurantId) return;
        setMenuSourceLoadError('');
        setJreatsMenuMeta(null);
        try {
            const token = localStorage.getItem('Token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            if (menuTypeFilter === 'catering') {
                // GET /catering/menu/:restaurantId → { items: [ { cateringItemId, foodType, itemName, price, quantity, imagePath, imageUrl, ... } ] }
                const response = await axiosRestaurantInstance.get(`/catering/menu/${restaurantId}`, { headers });
                const data = response.data || {};
                const items = data.items && Array.isArray(data.items) ? data.items : (data.cateringItemId != null ? [data] : Object.values(data).flat());
                const grouped = {};
                (Array.isArray(items) ? items : []).forEach((item) => {
                    const cat =
                        item.foodCategory ??
                        item.foodCategoryName ??
                        item.Food_Category ??
                        item.food_category ??
                        'Other';
                    if (!grouped[cat]) grouped[cat] = [];
                    const { imagePath, imageUrl } = resolveFoodItemImageFields(item);
                    grouped[cat].push({
                        Food_Item_Id: item.cateringItemId ?? item.Catering_Item_Id ?? item.Food_Item_Id,
                        cateringItemId: item.cateringItemId,
                        foodCategoryId: item.foodCategoryId ?? item.food_category_id,
                        foodCategory: cat,
                        Food_Category: cat,
                        Food_Type: cat,
                        itemFoodType: item.foodType ?? item.itemFoodType ?? 'VEG',
                        Food_Item: item.itemName ?? item.Item_Name ?? item.item_name ?? item.Food_Item ?? item.foodItem ?? '',
                        Price: item.price ?? item.Price,
                        Quantity: item.quantity ?? item.servingSize ?? item.Serving_Size ?? item.Quantity ?? 'serves 1',
                        Description: item.description ?? item.Description ?? '',
                        isAvailable: item.isAvailable ?? item.Is_available ?? item.is_available ?? true,
                        imagePath,
                        imageUrl: imageUrl || undefined,
                    });
                });
                setMenuItems(grouped);
            } else {
                const { items, meta } = await loadGroupedOnlineMenuItems(
                    restaurantId,
                    menuSourceFilter,
                    salesChannelFilter ? { salesChannel: salesChannelFilter } : {}
                );
                setJreatsMenuMeta(meta);
                setMenuItems(items);
            }
        } catch (error) {
            console.error('Error fetching menu items:', error);
            const msg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                'Failed to load menu items.';
            setMenuSourceLoadError(msg);
            setMenuItems({});
        }
    };

    useEffect(() => {
        if (menuTypeFilter !== 'online' || !restaurantId) return;
        fetchMenuItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when source or restaurant changes
    }, [menuSourceFilter, salesChannelFilter, restaurantId, menuTypeFilter]);

    // Modify handleAddNewItem to include fetching updated menu items (parent already refetches for catering; we refetch here for online)
    const handleAddNewItemWithRefresh = async (event) => {
        event.preventDefault();
        await handleAddNewItem(event);
        await fetchMenuItems();
    };
    

    const showImageColumn =
        menuTypeFilter === 'online' || menuTypeFilter === 'catering';
    const menuTableColSpan = useMemo(() => {
        let cols = showImageColumn ? 1 : 0;
        cols += 4;
        if (menuSourceFilter === 'JREATS') cols -= 1;
        if (isOnlineMenu) cols += 2;
        if (isCateringMenu) cols += 1;
        cols += 1;
        if (isOnlineMenu) cols += 1;
        if (isOnlineMenu) cols += 1;
        if (isCateringMenu) cols += 1;
        cols += 2;
        return cols;
    }, [showImageColumn, isOnlineMenu, isCateringMenu, menuSourceFilter]);

    return (
        <div className="menu orders-page">
            <div className="today-order-toolbar today-order-toolbar--menu">
                <div className="today-order-toolbar__row">
                    <div className="today-order-toolbar__main">
                        {(isOnlineMenu || isCateringMenu) && (
                            <CollapsibleFilters>
                                <ToolbarRichSelect
                                    panelId="menu-food-category-filter"
                                    value={foodCategoryFilter}
                                    onChange={handleFoodCategoryFilterChange}
                                    options={menuCategoryFilterRichOptions}
                                    ariaLabel="Filter by food category"
                                    panelTitle="Food category"
                                />
                                <ToolbarRichSelect
                                    panelId="menu-food-type-filter"
                                    value={foodTypeFilter}
                                    onChange={handleFoodTypeFilterChange}
                                    options={MENU_FOOD_TYPE_FILTER_OPTIONS}
                                    ariaLabel="Filter by food type"
                                    panelTitle="Food type"
                                    narrow
                                />
                                <ToolbarRichSelect
                                    panelId="menu-status-filter"
                                    value={statusFilter}
                                    onChange={handleStatusFilterChange}
                                    options={MENU_STATUS_FILTER_OPTIONS}
                                    ariaLabel="Filter by status"
                                    panelTitle="Status"
                                    narrow
                                />
                            </CollapsibleFilters>
                        )}
                    </div>
                    <div className="addmenu-button">
                        {isJreatsCatalogReadOnly ? null : (
                            <>
                                {canUseMasterPicker ? (
                                    <button
                                        className="add-button"
                                        type="button"
                                        onClick={() => setShowMasterPicker(true)}
                                        style={{ marginRight: 8 }}
                                    >
                                        From master
                                    </button>
                                ) : null}
                                <button className="add-button" type="button" onClick={handleAddMenu}>
                                    Add item
                                </button>
                            </>
                        )}
                    </div>
                </div>
                {isOnlineMenu && menuSourceFilter === 'JREATS' && jreatsMenuMeta?.state ? (
                    <p className="menu-template-jreats-state-hint menu-toolbar-jreats-hint">
                        Junior Eats statewide menu for <strong>{jreatsMenuMeta.state}</strong>
                        {' — '}managed in Eats Admin. Use Daily Menu / Templates to publish.
                    </p>
                ) : isOnlineMenu && menuSourceFilter === 'JREATS' ? (
                    <p className="menu-template-jreats-state-hint menu-toolbar-jreats-hint">
                        Junior Eats statewide menu — managed in Eats Admin. Use Daily Menu / Templates to publish.
                    </p>
                ) : null}
                {isOnlineMenu && menuSourceLoadError ? (
                    <p className="menu-template-items-load-error menu-toolbar-menu-error">
                        {menuSourceLoadError}
                    </p>
                ) : null}
            </div>

            {renderMenuItemModal({
                open: showAddMenu,
                title: 'Add menu item',
                titleId: 'menu-item-add-modal-title',
                onClose: closeAddModal,
                onSubmit: handleAddNewItemWithRefresh,
                submitLabel: 'Add',
                formMode: 'add',
            })}

            {renderMenuItemModal({
                open: Boolean(editingItem && editDraft),
                title: `Edit menu item${editDraft?.Food_Item ? ` — ${editDraft.Food_Item}` : ''}`,
                titleId: 'menu-item-edit-modal-title',
                onClose: closeEditModal,
                onSubmit: submitEdit,
                submitLabel: 'Update',
                formMode: 'edit',
            })}

            {canUseMasterPicker ? (
                <MasterMenuPicker
                    open={showMasterPicker}
                    onClose={() => setShowMasterPicker(false)}
                    restaurantId={restaurantId}
                    menuSource={menuSourceFilter === 'DINEIN' ? 'DINEIN' : 'MYEATS'}
                    createdById={createdById}
                    categories={foodCategories}
                    onAdopted={async () => {
                        try {
                            const { items } = await loadGroupedOnlineMenuItems(
                                restaurantId,
                                menuSourceFilter === 'DINEIN' ? 'DINEIN' : 'MYEATS',
                                { salesChannel: salesChannelFilter || undefined }
                            );
                            if (typeof setMenuItems === 'function') {
                                setMenuItems(items || {});
                            }
                        } catch (err) {
                            setMenuSourceLoadError(
                                err?.response?.data?.error || err?.message || 'Failed to reload menu'
                            );
                        }
                    }}
                />
            ) : null}

            <OrdersTablePanel
                searchValue={foodItemSearch}
                onSearchChange={setFoodItemSearch}
                resultCount={sortedMenuItemsFlat.length}
                resultLabel="item"
                searchAriaLabel="Search menu items"
            >
                <table className="menu-table">
                        <thead>
                            <tr>
                                {showImageColumn && (
                                    <th className="menu-table-image-col" aria-hidden="true" />
                                )}
                                <OrdersSortableTh
                                    label="Food Item"
                                    sortKey="foodItem"
                                    activeSortKey={menuSort?.key}
                                    sortDirection={menuSort?.direction}
                                    onSortAsc={handleMenuSortAsc}
                                    onSortDesc={handleMenuSortDesc}
                                />
                                <OrdersSortableTh
                                    label="Food Category"
                                    sortKey="category"
                                    activeSortKey={menuSort?.key}
                                    sortDirection={menuSort?.direction}
                                    onSortAsc={handleMenuSortAsc}
                                    onSortDesc={handleMenuSortDesc}
                                />
                                <OrdersSortableTh
                                    label={
                                        menuSourceFilter === 'JREATS'
                                            ? 'Price & Quantity'
                                            : 'Price'
                                    }
                                    sortKey="price"
                                    activeSortKey={menuSort?.key}
                                    sortDirection={menuSort?.direction}
                                    onSortAsc={handleMenuSortAsc}
                                    onSortDesc={handleMenuSortDesc}
                                />
                                {menuSourceFilter === 'JREATS' ? null : (
                                    <OrdersSortableTh
                                        label="Quantity"
                                        sortKey="quantity"
                                        activeSortKey={menuSort?.key}
                                        sortDirection={menuSort?.direction}
                                        onSortAsc={handleMenuSortAsc}
                                        onSortDesc={handleMenuSortDesc}
                                    />
                                )}
                                {isOnlineMenu && (
                                    <OrdersSortableTh
                                        label="Food Type"
                                        sortKey="foodType"
                                        activeSortKey={menuSort?.key}
                                        sortDirection={menuSort?.direction}
                                        onSortAsc={handleMenuSortAsc}
                                        onSortDesc={handleMenuSortDesc}
                                    />
                                )}
                                {isCateringMenu && (
                                    <OrdersSortableTh
                                        label="Food Type"
                                        sortKey="foodType"
                                        activeSortKey={menuSort?.key}
                                        sortDirection={menuSort?.direction}
                                        onSortAsc={handleMenuSortAsc}
                                        onSortDesc={handleMenuSortDesc}
                                    />
                                )}
                                {isOnlineMenu && (
                                    <OrdersSortableTh
                                        label="Menu Source"
                                        sortKey="menuSource"
                                        activeSortKey={menuSort?.key}
                                        sortDirection={menuSort?.direction}
                                        onSortAsc={handleMenuSortAsc}
                                        onSortDesc={handleMenuSortDesc}
                                    />
                                )}
                                {isCateringMenu && (
                                    <OrdersSortableTh
                                        label="Available"
                                        sortKey="available"
                                        activeSortKey={menuSort?.key}
                                        sortDirection={menuSort?.direction}
                                        onSortAsc={handleMenuSortAsc}
                                        onSortDesc={handleMenuSortDesc}
                                    />
                                )}
                                <OrdersSortableTh
                                    label="Description"
                                    sortKey="description"
                                    activeSortKey={menuSort?.key}
                                    sortDirection={menuSort?.direction}
                                    onSortAsc={handleMenuSortAsc}
                                    onSortDesc={handleMenuSortDesc}
                                />
                                {isOnlineMenu && (
                                    <OrdersSortableTh
                                        label="Ingredients"
                                        sortKey="ingredients"
                                        activeSortKey={menuSort?.key}
                                        sortDirection={menuSort?.direction}
                                        onSortAsc={handleMenuSortAsc}
                                        onSortDesc={handleMenuSortDesc}
                                    />
                                )}
                                {isOnlineMenu && (
                                    <OrdersSortableTh
                                        label="Status"
                                        sortKey="status"
                                        activeSortKey={menuSort?.key}
                                        sortDirection={menuSort?.direction}
                                        onSortAsc={handleMenuSortAsc}
                                        onSortDesc={handleMenuSortDesc}
                                    />
                                )}
                                <th>Recipe</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedMenuItemsFlat.length > 0 ? (
                            paginatedMenuItemsFlat.map(({ item, category }, index) => (
                                <tr key={item.Food_Item_Id ?? `${category}-${item.Food_Item}-${index}`}>
                                    {showImageColumn && (
                                        <td>
                                            <img
                                                className="menu-table-thumb"
                                                src={getDisplayImageUrl(item) || defaultFoodImage}
                                                alt={item.Food_Item}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = defaultFoodImage;
                                                }}
                                            />
                                        </td>
                                    )}
                                    <td className="menu-table-food-name">
                                        {onFoodItemNavigate ? (
                                            <button
                                                type="button"
                                                className="menu-table-food-name-link"
                                                onClick={() =>
                                                    onFoodItemNavigate(getMenuItemRecipeId(item))
                                                }
                                            >
                                                {formatFoodItemDisplayName(item.Food_Item)}
                                            </button>
                                        ) : (
                                            formatFoodItemDisplayName(item.Food_Item)
                                        )}
                                    </td>
                                    <td className="menu-table-cell-muted">{category}</td>
                                    <td>
                                        {menuSourceFilter === 'JREATS' ? (
                                            <AgePricingComboCell
                                                item={item}
                                                fallbackPrice={item.Price}
                                                fallbackQuantity={item.Quantity}
                                                currency={item.Currency ?? item.currency}
                                            />
                                        ) : (
                                            formatPriceWithCurrency(
                                                item.Price,
                                                item.Currency ?? item.currency
                                            )
                                        )}
                                    </td>
                                    {menuSourceFilter === 'JREATS' ? null : (
                                        <td>{item.Quantity}</td>
                                    )}
                                    {isOnlineMenu && <td>{formatDietTypeLabel(item.itemFoodType)}</td>}
                                    {isCateringMenu && <td>{formatDietTypeLabel(item.itemFoodType)}</td>}
                                    {isOnlineMenu && <td>{item.Menu_Source ?? ''}</td>}
                                    {isCateringMenu && <td>{formatAvailabilityLabel(item)}</td>}
                                    <td
                                        className="menu-table-cell-truncate"
                                        title={item.Description ?? item.description ?? ''}
                                    >
                                        {item.Description ?? item.description ?? ''}
                                    </td>
                                    {isOnlineMenu && (
                                    <td className="menu-table-cell-ingredients">
                                        {(() => {
                                            const ingredientsList = ingredientsListFromMenuItem(item);
                                            const ingredientsDisplay =
                                                ingredientsList.join(', ') ||
                                                formatOnlineIngredientsDisplay(item);
                                            if (!ingredientsDisplay) {
                                                return '—';
                                            }
                                            const ingredientsPreview = formatIngredientsTablePreview(
                                                ingredientsList,
                                                ingredientsDisplay
                                            );
                                            return (
                                                <div className="menu-ingredients-preview-wrap">
                                                    <span
                                                        className="menu-ingredients-inline"
                                                        title={ingredientsDisplay}
                                                    >
                                                        {ingredientsPreview}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="menu-ingredients-info-btn"
                                                        aria-label={`View ingredients for ${item.Food_Item}`}
                                                        title="View ingredients"
                                                        onClick={() =>
                                                            setIngredientsPopup({
                                                                foodItemName: item.Food_Item ?? 'Item',
                                                                ingredients:
                                                                    ingredientsList.length > 0
                                                                        ? ingredientsList
                                                                        : ingredientsListFromMenuItem(item),
                                                            })
                                                        }
                                                    >
                                                        <FontAwesomeIcon icon={faCircleInfo} />
                                                    </button>
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    )}
                                    {isOnlineMenu && (
                                        <td>
                                            <span
                                                className={`menu-template-status-badge menu-template-status-badge--${
                                                    isMenuItemActiveStatus(item.Status ?? item.status)
                                                        ? 'active'
                                                        : 'inactive'
                                                }`}
                                            >
                                                {formatMenuItemStatusLabel(item.Status ?? item.status)}
                                            </span>
                                        </td>
                                    )}
                                    <td className="menu-table-recipe-cell">
                                        {isJreatsCatalogReadOnly ? (
                                            '—'
                                        ) : (
                                            <button
                                                type="button"
                                                className="menu-recipe-action-btn"
                                                onClick={() =>
                                                    handleRecipeAction(getMenuItemRecipeId(item))
                                                }
                                            >
                                                {menuItemHasRecipe(item) ? 'View' : 'Add'}
                                            </button>
                                        )}
                                    </td>
                                    <td className="menu-table-actions-cell">
                                        {isJreatsCatalogReadOnly ? (
                                            '—'
                                        ) : (
                                            <>
                                                <button className="action-button" onClick={() => handleEditClick(item)}>
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                {((isOnlineMenu &&
                                                    isMenuItemActiveStatus(item.Status ?? item.status)) ||
                                                    (isCateringMenu && isCateringItemAvailable(item))) && (
                                                    <button
                                                        className="action-button delete-button"
                                                        onClick={() => handleDeleteMenuItem(item.Food_Item_Id)}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={menuTableColSpan}
                                        className="no-orders-message menu-table-empty-cell menu-empty-filtered"
                                    >
                                        {foodSearchQuery || hasActiveMenuFilters
                                            ? 'No menu items match your filters.'
                                            : 'No menu items available.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </OrdersTablePanel>

            {sortedMenuItemsFlat.length > 0 ? (
                <OrdersTablePagination
                    currentPage={menuPageSafe}
                    totalPages={menuTotalPages}
                    totalItems={sortedMenuItemsFlat.length}
                    pageSize={menuPageSize}
                    onPageChange={handleMenuPageChange}
                    onPageSizeChange={handleMenuPageSizeChange}
                    itemLabel="items"
                    ariaLabel="Menu table pagination"
                />
            ) : null}

            {ingredientsPopup && (
                <div
                    className="inventory-modal-overlay"
                    role="presentation"
                    onClick={() => setIngredientsPopup(null)}
                >
                    <div
                        className="inventory-modal menu-ingredients-modal"
                        role="dialog"
                        aria-labelledby="menu-ingredients-popup-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="inventory-modal-header">
                            <h4 id="menu-ingredients-popup-title">
                                Ingredients — {formatFoodItemDisplayName(ingredientsPopup.foodItemName)}
                            </h4>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                aria-label="Close"
                                onClick={() => setIngredientsPopup(null)}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        {ingredientsPopup.ingredients.length > 0 ? (
                            <ul className="menu-ingredients-popup-list">
                                {ingredientsPopup.ingredients.map((name, index) => (
                                    <li key={`${name}-${index}`}>{name}</li>
                                ))}
                            </ul>
                        ) : (
                            <p className="menu-ingredients-popup-empty menu-empty-filtered">
                                No ingredients listed.
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Menu;
